import { useState, useEffect, useMemo } from "react";
import { getCDLChapters, getAllCDLPages } from "../data/cdlMasteryLabContent";
import "./CDLTutorial.css";

export default function CDLTutorial({ onHome }) {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [bookmarks, setBookmarks] = useState([]);
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  const [expandedChapters, setExpandedChapters] = useState({});

  // Load chapters from exact content data
  const chapters = getCDLChapters();

  // Flatten all pages for easier navigation
  const allPages = useMemo(() => {
    return chapters.flatMap(chapter => chapter.pages);
  }, [chapters]);

  const currentPage = allPages[currentPageIndex];

  // Search functionality
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return allPages.filter(page =>
      page.title.toLowerCase().includes(query) ||
      page.content.toLowerCase().includes(query)
    ).map((page, idx) => ({
      ...page,
      matchIndex: allPages.indexOf(page)
    }));
  }, [searchQuery, allPages]);

  const toggleBookmark = (pageNum) => {
    setBookmarks(prev =>
      prev.includes(pageNum)
        ? prev.filter(p => p !== pageNum)
        : [...prev, pageNum]
    );
  };

  const handlePageJump = (index) => {
    setCurrentPageIndex(Math.max(0, Math.min(index, allPages.length - 1)));
    setShowTableOfContents(false);
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
  };

  return (
    <div className="cdl-tutorial-container">
      {/* Header */}
      <header className="tutorial-header">
        <button className="back-btn" onClick={onHome}>← Back to Home</button>
        <h1>📚 CDL Skills Mastery Lab - Student Guide</h1>
        <div className="header-info">
          <span>Page {currentPageIndex + 1} of {allPages.length}</span>
          <button
            className={`bookmark-btn ${bookmarks.includes(currentPage.pageNum) ? "active" : ""}`}
            onClick={() => toggleBookmark(currentPage.pageNum)}
            title="Bookmark this page"
          >
            ⭐
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="tutorial-main">
        {/* Sidebar */}
        <aside className="tutorial-sidebar">
          <div className="sidebar-section">
            <h3>Search</h3>
            <input
              type="text"
              className="search-input"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && searchResults.length > 0 && (
              <div className="search-results">
                <p className="results-count">{searchResults.length} results</p>
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    className="search-result-item"
                    onClick={() => handlePageJump(result.matchIndex)}
                  >
                    <strong>Page {result.pageNum}</strong>
                    <span>{result.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <h3>Bookmarks</h3>
            {bookmarks.length > 0 ? (
              <div className="bookmarks-list">
                {bookmarks.map(pageNum => {
                  const bookmarkedPage = allPages.find(p => p.pageNum === pageNum);
                  return bookmarkedPage ? (
                    <button
                      key={pageNum}
                      className="bookmark-item"
                      onClick={() => handlePageJump(allPages.indexOf(bookmarkedPage))}
                    >
                      <span className="bookmark-num">Page {pageNum}</span>
                      <span className="bookmark-title">{bookmarkedPage.title}</span>
                    </button>
                  ) : null;
                })}
              </div>
            ) : (
              <p className="empty-message">No bookmarks yet</p>
            )}
          </div>

          <div className="sidebar-section">
            <button
              className="toc-toggle"
              onClick={() => setShowTableOfContents(!showTableOfContents)}
            >
              {showTableOfContents ? "Hide" : "Show"} Table of Contents
            </button>
            {showTableOfContents && (
              <div className="table-of-contents">
                {chapters.map(chapter => (
                  <div key={chapter.id} className="toc-chapter">
                    <button
                      className="toc-chapter-title"
                      onClick={() => toggleChapter(chapter.id)}
                    >
                      {expandedChapters[chapter.id] ? "▼" : "▶"} {chapter.title}
                    </button>
                    {expandedChapters[chapter.id] && (
                      <div className="toc-pages">
                        {chapter.pages.map((page, idx) => (
                          <button
                            key={idx}
                            className={`toc-page ${
                              currentPageIndex === allPages.indexOf(page) ? "active" : ""
                            }`}
                            onClick={() => handlePageJump(allPages.indexOf(page))}
                          >
                            Page {page.pageNum}: {page.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Content Area */}
        <main className="tutorial-content">
          <article className="page-container">
            <div className="page-header">
              <span className="page-num">Page {currentPage.pageNum}</span>
              <h2>{currentPage.title}</h2>
            </div>

            {/* Display Icon Guide */}
            {currentPage.isIconGuide && currentPage.icons && (
              <div className="icon-guide-display">
                <div className="icon-guide-header">
                  <h2>Training Guide Icons</h2>
                  <p className="icon-guide-subtitle">These icons are cues for information types and activities in this document.</p>
                </div>

                <div className="icons-list">
                  {currentPage.icons.map((icon, idx) => (
                    <div key={idx} className={`icon-row icon-row-${icon.icon}`}>
                      <div className="icon-wrapper">
                        <div className="icon-bg"></div>
                        <svg className={`icon-svg icon-${icon.icon}`} viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
                          {icon.icon === "safety" && (
                            <path d="M30 8C18 8 12 16 12 24C12 32 24 50 30 50C36 50 48 32 48 24C48 16 42 8 30 8Z" fill="currentColor" strokeWidth="0"/>
                          )}
                          {icon.icon === "warning" && (
                            <>
                              <polygon points="30,6 54,48 6,48" fill="currentColor" strokeWidth="0"/>
                              <circle cx="30" cy="38" r="2.5" fill="white"/>
                              <rect x="28.5" y="26" width="3" height="9" fill="white"/>
                            </>
                          )}
                          {icon.icon === "activity" && (
                            <>
                              <rect x="10" y="14" width="3.5" height="32" fill="currentColor"/>
                              <rect x="17" y="8" width="3.5" height="38" fill="currentColor"/>
                              <rect x="24" y="18" width="3.5" height="28" fill="currentColor"/>
                              <line x1="35" y1="6" x2="52" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                              <line x1="35" y1="20" x2="52" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                              <line x1="35" y1="34" x2="52" y2="34" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            </>
                          )}
                          {icon.icon === "knowledge" && (
                            <>
                              <path d="M30 12C20 12 12 20 12 30C12 40 20 48 30 48C40 48 48 40 48 30C48 20 40 12 30 12Z" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                              <text x="30" y="37" textAnchor="middle" fontSize="22" fontWeight="bold" fill="currentColor">?</text>
                            </>
                          )}
                          {icon.icon === "discussion" && (
                            <>
                              <rect x="8" y="14" width="20" height="16" rx="3" fill="currentColor"/>
                              <polygon points="14,31 20,37 26,31" fill="currentColor"/>
                              <circle cx="37" cy="24" r="2.5" fill="currentColor"/>
                              <circle cx="44" cy="24" r="2.5" fill="currentColor"/>
                              <circle cx="51" cy="24" r="2.5" fill="currentColor"/>
                            </>
                          )}
                          {icon.icon === "resources" && (
                            <>
                              <rect x="10" y="10" width="9" height="24" rx="1" fill="currentColor"/>
                              <rect x="22" y="8" width="9" height="26" rx="1" fill="currentColor"/>
                              <rect x="34" y="14" width="9" height="20" rx="1" fill="currentColor"/>
                              <line x1="8" y1="38" x2="46" y2="38" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            </>
                          )}
                          {icon.icon === "video" && (
                            <>
                              <rect x="10" y="16" width="40" height="26" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                              <polygon points="24,22 24,40 42,31" fill="currentColor"/>
                            </>
                          )}
                        </svg>
                      </div>
                      <div className="icon-content">
                        <h3 className="icon-label">{icon.label}</h3>
                        <p className="icon-description">{icon.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Display Table of Contents */}
            {currentPage.isTOC && currentPage.tocSections && (
              <div className="toc-page-display">
                <div className="toc-header">
                  <h2>Table of Contents</h2>
                  <p className="toc-subtitle">CDL Skills Mastery Lab — Complete Course Guide</p>
                </div>

                <div className="toc-sections">
                  {currentPage.tocSections.map((section, idx) => (
                    <div key={idx} className="toc-section">
                      <div className="toc-section-header" style={{ borderLeftColor: section.color }}>
                        <span className="toc-section-label">{section.label}</span>
                      </div>
                      <ul className="toc-entries">
                        {section.entries.map((entry, entryIdx) => (
                          <li key={entryIdx} className="toc-entry">
                            <span className="toc-title">{entry.title}</span>
                            <span className="toc-leader"></span>
                            <span className="toc-page">{entry.page}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Display cover page */}
            {currentPage.isCover && currentPage.coverElements && (
              <div className="cover-page-display">
                <div className="cover-header">
                  <h1 className="cover-title">{currentPage.coverElements.title}</h1>
                  <p className="cover-subtitle">{currentPage.coverElements.subtitle}</p>
                </div>

                {/* Display actual image if available */}
                {currentPage.images && currentPage.images[0]?.hasActualImage ? (
                  <div className="cover-actual-image">
                    <div className="cover-banner-visual">
                      <div className="banner-section street-scene">
                        <div className="scene-content">
                          🏙️ Street Scene
                        </div>
                      </div>
                      <div className="banner-section orange-divider"></div>
                      <div className="banner-section bus-rear">
                        <div className="scene-content">
                          🚌 Bus Rear
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="cover-images-strip">
                    <div className="cover-image-slot top">
                      <div className="bus-image-placeholder">
                        🚌 {currentPage.coverElements.images[0]?.description}
                      </div>
                    </div>
                    <div className="cover-image-slot middle">
                      <div className="orange-divider"></div>
                    </div>
                    <div className="cover-image-slot bottom">
                      <div className="bus-image-placeholder">
                        🚌 {currentPage.coverElements.images[1]?.description}
                      </div>
                    </div>
                  </div>
                )}

                <div className="cover-footer">
                  <div className="cover-org">
                    <strong>{currentPage.coverElements.organization}</strong>
                  </div>
                  <div className="cover-meta">
                    <p>{currentPage.coverElements.version}</p>
                  </div>
                  <div className="cover-logo">🚌</div>
                </div>
              </div>
            )}

            {/* Display images if page has them */}
            {currentPage.images && currentPage.images.length > 0 && !currentPage.isCover && (
              <div className="page-images">
                {currentPage.images.map((img, idx) => (
                  <div key={idx} className="page-image-container">
                    {img.type === "banner" && (
                      <>
                        <div className="image-placeholder-banner">
                          🖼️ {img.description}
                        </div>
                        <p className="image-caption">{img.alt}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Display visual elements if page has them */}
            {currentPage.hasVisuals && currentPage.visualElements && (
              <div className="page-visuals">
                {currentPage.visualElements.map((element, idx) => {
                  if (element.type === "icon") {
                    return (
                      <div key={idx} className="visual-icon-item">
                        <div className="icon-symbol">{element.symbol}</div>
                        <div className="icon-label">{element.label}</div>
                        <div className="icon-desc">{element.description}</div>
                      </div>
                    );
                  } else if (element.type === "box") {
                    return (
                      <div key={idx} className="visual-box-item">
                        <div className="box-icon">{element.icon}</div>
                        <div className="box-label">{element.label}</div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}

            {/* Display visual description if exists */}
            {currentPage.visualDescription && (
              <div className="visual-description">
                📸 {currentPage.visualDescription}
              </div>
            )}

            {/* Display page header if exists */}
            {currentPage.pageHeader && (
              <div className="page-header-line">
                <span className="header-left">Agenda</span>
                <span className="header-center">This course contains the following sessions</span>
              </div>
            )}

            {/* Display table if exists */}
            {currentPage.hasTable && currentPage.tableData && (
              <div className="page-table">
                {currentPage.tableData.title && (
                  <h3 className="table-title">{currentPage.tableData.title}</h3>
                )}
                <table className="content-table">
                  {currentPage.tableData.headers && currentPage.tableData.headers.length > 0 && (
                    <thead>
                      <tr>
                        {currentPage.tableData.headers.map((header, idx) => (
                          <th key={idx}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {currentPage.tableData.rows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Display two-column layout with boxes under Overview */}
            {currentPage.overviewContent && currentPage.whyMattersContent && (
              <div className="two-column-layout">
                <div className="column-left">
                  <h3>Overview</h3>
                  {currentPage.overviewContent.split("\n").map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}

                  {/* Display boxes under Overview if flagged */}
                  {currentPage.boxesAfterOverview && currentPage.visualElements && (
                    <div className="boxes-row">
                      {currentPage.visualElements.map((element, idx) => (
                        <div key={idx} className="progress-box">
                          <div className="box-icon">{element.icon}</div>
                          <div className="box-label">{element.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="column-right">
                  <h3>Why This Matters</h3>
                  {currentPage.whyMattersContent.split("\n").map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </div>
            )}

            <div className="page-body">
              {currentPage.content.split("\n").map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </article>

          {/* Navigation Controls */}
          <div className="page-navigation">
            <button
              className="nav-btn"
              onClick={() => handlePageJump(currentPageIndex - 1)}
              disabled={currentPageIndex === 0}
            >
              ← Previous
            </button>

            <div className="page-indicator">
              <input
                type="number"
                min="1"
                max={allPages.length}
                value={currentPageIndex + 1}
                onChange={(e) => {
                  const pageNum = parseInt(e.target.value) - 1;
                  if (pageNum >= 0 && pageNum < allPages.length) {
                    handlePageJump(pageNum);
                  }
                }}
                className="page-input"
              />
              <span>/ {allPages.length}</span>
            </div>

            <button
              className="nav-btn"
              onClick={() => handlePageJump(currentPageIndex + 1)}
              disabled={currentPageIndex === allPages.length - 1}
            >
              Next →
            </button>
          </div>
        </main>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${((currentPageIndex + 1) / allPages.length) * 100}%` }}
        ></div>
      </div>
    </div>
  );
}
